<!--
  Performance Metrics Center
  Centro de métricas de rendimiento para formularios optimizados
-->

<template>
  <div class="performance-metrics-center">
    <v-container fluid class="pa-6">
      <!-- Header -->
      <v-row class="mb-4">
        <v-col cols="12">
          <v-card class="elevation-8">
            <v-card-title class="bg-gradient-primary text-white pa-4">
              <v-icon class="mr-3" size="32">mdi-speedometer</v-icon>
              <div>
                <h2 class="text-h4 mb-1">Performance Metrics Center</h2>
                <p class="text-body-1 mb-0 opacity-90">
                  Monitoreo de rendimiento para formularios optimizados
                </p>
              </div>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>

      <!-- Métricas en Tiempo Real -->
      <v-row class="mb-4">
        <v-col cols="12" md="3">
          <v-card class="elevation-6 h-100">
            <v-card-title class="bg-primary text-white text-center">
              <v-icon class="mr-2">mdi-timer</v-icon>
              Load Time
            </v-card-title>
            <v-card-text class="text-center pa-4">
              <h2 class="text-h3 text-primary font-weight-bold">
                {{ metrics.loadTime }}ms
              </h2>
              <p class="text-body-2 mb-2">Tiempo de Carga</p>
              <v-chip
                :color="getLoadTimeColor(metrics.loadTime)"
                size="small"
                variant="elevated"
              >
                {{ getLoadTimeStatus(metrics.loadTime) }}
              </v-chip>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="3">
          <v-card class="elevation-6 h-100">
            <v-card-title class="bg-secondary text-white text-center">
              <v-icon class="mr-2">mdi-memory</v-icon>
              Memory Usage
            </v-card-title>
            <v-card-text class="text-center pa-4">
              <h2 class="text-h3 text-secondary font-weight-bold">
                {{ metrics.memoryUsage }}MB
              </h2>
              <p class="text-body-2 mb-2">Uso de Memoria</p>
              <v-chip
                :color="getMemoryColor(metrics.memoryUsage)"
                size="small"
                variant="elevated"
              >
                {{ getMemoryStatus(metrics.memoryUsage) }}
              </v-chip>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="3">
          <v-card class="elevation-6 h-100">
            <v-card-title class="bg-success text-white text-center">
              <v-icon class="mr-2">mdi-check-circle</v-icon>
              Validation Speed
            </v-card-title>
            <v-card-text class="text-center pa-4">
              <h2 class="text-h3 text-success font-weight-bold">
                {{ metrics.validationSpeed }}ms
              </h2>
              <p class="text-body-2 mb-2">Velocidad Validación</p>
              <v-chip
                :color="getValidationColor(metrics.validationSpeed)"
                size="small"
                variant="elevated"
              >
                {{ getValidationStatus(metrics.validationSpeed) }}
              </v-chip>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="3">
          <v-card class="elevation-6 h-100">
            <v-card-title class="bg-warning text-white text-center">
              <v-icon class="mr-2">mdi-lazy-loading</v-icon>
              Lazy Loading
            </v-card-title>
            <v-card-text class="text-center pa-4">
              <h2 class="text-h3 text-warning font-weight-bold">
                {{ metrics.lazyLoadedComponents }}
              </h2>
              <p class="text-body-2 mb-2">Componentes Lazy</p>
              <v-chip
                color="success"
                size="small"
                variant="elevated"
              >
                ACTIVO
              </v-chip>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Gráficos de Performance -->
      <v-row class="mb-4">
        <v-col cols="12" md="8">
          <v-card class="elevation-6">
            <v-card-title class="bg-info text-white">
              <v-icon class="mr-2">mdi-chart-line</v-icon>
              Performance Timeline
            </v-card-title>
            <v-card-text class="pa-4">
              <div class="chart-container" style="height: 300px;">
                <canvas ref="performanceChart"></canvas>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card class="elevation-6 h-100">
            <v-card-title class="bg-purple text-white">
              <v-icon class="mr-2">mdi-gauge</v-icon>
              Performance Score
            </v-card-title>
            <v-card-text class="pa-4">
              <div class="text-center">
                <v-progress-circular
                  :model-value="performanceScore"
                  :color="getScoreColor(performanceScore)"
                  size="120"
                  width="8"
                  class="mb-3"
                >
                  <span class="text-h4 font-weight-bold">
                    {{ performanceScore }}
                  </span>
                </v-progress-circular>
                
                <h3 class="mb-3">Performance Score</h3>
                
                <v-list density="compact">
                  <v-list-item>
                    <template v-slot:prepend>
                      <v-icon color="success">mdi-check</v-icon>
                    </template>
                    <v-list-item-title>Async Validation</v-list-item-title>
                  </v-list-item>
                  
                  <v-list-item>
                    <template v-slot:prepend>
                      <v-icon color="success">mdi-check</v-icon>
                    </template>
                    <v-list-item-title>Lazy Loading</v-list-item-title>
                  </v-list-item>
                  
                  <v-list-item>
                    <template v-slot:prepend>
                      <v-icon color="success">mdi-check</v-icon>
                    </template>
                    <v-list-item-title>Code Splitting</v-list-item-title>
                  </v-list-item>
                  
                  <v-list-item>
                    <template v-slot:prepend>
                      <v-icon color="warning">mdi-clock</v-icon>
                    </template>
                    <v-list-item-title>Bundle Optimization</v-list-item-title>
                  </v-list-item>
                </v-list>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Detalles de Optimizaciones -->
      <v-row>
        <v-col cols="12">
          <v-card class="elevation-6">
            <v-card-title class="bg-gradient text-white">
              <v-icon class="mr-2">mdi-tune</v-icon>
              Optimizaciones Activas
            </v-card-title>
            <v-card-text class="pa-4">
              <v-row>
                <v-col cols="12" md="4">
                  <h4 class="mb-3">📝 Form Optimizations</h4>
                  <v-list density="compact">
                    <v-list-item>
                      <template v-slot:prepend>
                        <v-icon color="success">mdi-check-circle</v-icon>
                      </template>
                      <v-list-item-title>Debounced Validation</v-list-item-title>
                      <v-list-item-subtitle>300ms delay</v-list-item-subtitle>
                    </v-list-item>
                    
                    <v-list-item>
                      <template v-slot:prepend>
                        <v-icon color="success">mdi-check-circle</v-icon>
                      </template>
                      <v-list-item-title>Async Field Validation</v-list-item-title>
                      <v-list-item-subtitle>Email & Username</v-list-item-subtitle>
                    </v-list-item>
                    
                    <v-list-item>
                      <template v-slot:prepend>
                        <v-icon color="success">mdi-check-circle</v-icon>
                      </template>
                      <v-list-item-title>Smart Error Messages</v-list-item-title>
                      <v-list-item-subtitle>Context-aware</v-list-item-subtitle>
                    </v-list-item>
                  </v-list>
                </v-col>

                <v-col cols="12" md="4">
                  <h4 class="mb-3">⚡ Performance Optimizations</h4>
                  <v-list density="compact">
                    <v-list-item>
                      <template v-slot:prepend>
                        <v-icon color="success">mdi-check-circle</v-icon>
                      </template>
                      <v-list-item-title>Component Lazy Loading</v-list-item-title>
                      <v-list-item-subtitle>On-demand imports</v-list-item-subtitle>
                    </v-list-item>
                    
                    <v-list-item>
                      <template v-slot:prepend>
                        <v-icon color="success">mdi-check-circle</v-icon>
                      </template>
                      <v-list-item-title>Device Optimization</v-list-item-title>
                      <v-list-item-subtitle>Mobile/Desktop aware</v-list-item-subtitle>
                    </v-list-item>
                    
                    <v-list-item>
                      <template v-slot:prepend>
                        <v-icon color="success">mdi-check-circle</v-icon>
                      </template>
                      <v-list-item-title>Network Adaptation</v-list-item-title>
                      <v-list-item-subtitle>Connection speed aware</v-list-item-subtitle>
                    </v-list-item>
                  </v-list>
                </v-col>

                <v-col cols="12" md="4">
                  <h4 class="mb-3">🔧 Technical Optimizations</h4>
                  <v-list density="compact">
                    <v-list-item>
                      <template v-slot:prepend>
                        <v-icon color="success">mdi-check-circle</v-icon>
                      </template>
                      <v-list-item-title>Bundle Splitting</v-list-item-title>
                      <v-list-item-subtitle>Chunk optimization</v-list-item-subtitle>
                    </v-list-item>
                    
                    <v-list-item>
                      <template v-slot:prepend>
                        <v-icon color="success">mdi-check-circle</v-icon>
                      </template>
                      <v-list-item-title>Tree Shaking</v-list-item-title>
                      <v-list-item-subtitle>Dead code elimination</v-list-item-subtitle>
                    </v-list-item>
                    
                    <v-list-item>
                      <template v-slot:prepend>
                        <v-icon color="warning">mdi-clock</v-icon>
                      </template>
                      <v-list-item-title>Service Worker</v-list-item-title>
                      <v-list-item-subtitle>En desarrollo</v-list-item-subtitle>
                    </v-list-item>
                  </v-list>
                </v-col>
              </v-row>

              <v-divider class="my-4"></v-divider>

              <!-- Controles de Performance -->
              <div class="d-flex gap-3 flex-wrap">
                <v-btn
                  color="primary"
                  variant="elevated"
                  prepend-icon="mdi-play"
                  @click="runPerformanceTest"
                  :loading="testingPerformance"
                >
                  Run Performance Test
                </v-btn>
                
                <v-btn
                  color="secondary"
                  variant="outlined"
                  prepend-icon="mdi-refresh"
                  @click="refreshMetrics"
                >
                  Refresh Metrics
                </v-btn>
                
                <v-btn
                  color="success"
                  variant="outlined"
                  prepend-icon="mdi-download"
                  @click="exportMetrics"
                >
                  Export Metrics
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, nextTick } from 'vue'

// Estado reactivo
const metrics = reactive({
  loadTime: 234,
  memoryUsage: 12.5,
  validationSpeed: 45,
  lazyLoadedComponents: 8
})

const performanceScore = ref(85)
const testingPerformance = ref(false)
const performanceChart = ref<HTMLCanvasElement>()

// Funciones de color/estado
const getLoadTimeColor = (time: number) => {
  if (time < 200) return 'success'
  if (time < 500) return 'warning'
  return 'error'
}

const getLoadTimeStatus = (time: number) => {
  if (time < 200) return 'EXCELENTE'
  if (time < 500) return 'BUENO'
  return 'LENTO'
}

const getMemoryColor = (memory: number) => {
  if (memory < 10) return 'success'
  if (memory < 20) return 'warning'
  return 'error'
}

const getMemoryStatus = (memory: number) => {
  if (memory < 10) return 'ÓPTIMO'
  if (memory < 20) return 'MODERADO'
  return 'ALTO'
}

const getValidationColor = (speed: number) => {
  if (speed < 50) return 'success'
  if (speed < 100) return 'warning'
  return 'error'
}

const getValidationStatus = (speed: number) => {
  if (speed < 50) return 'RÁPIDO'
  if (speed < 100) return 'NORMAL'
  return 'LENTO'
}

const getScoreColor = (score: number) => {
  if (score >= 90) return 'success'
  if (score >= 70) return 'warning'
  return 'error'
}

// Funciones principales
const runPerformanceTest = async () => {
  testingPerformance.value = true
  
  try {
    // Simular test de performance
    await new Promise(resolve => setTimeout(resolve, 2000))
    
    // Actualizar métricas simuladas
    metrics.loadTime = Math.floor(Math.random() * 300) + 100
    metrics.memoryUsage = Math.floor(Math.random() * 15) + 5
    metrics.validationSpeed = Math.floor(Math.random() * 80) + 20
    
    // Actualizar score
    performanceScore.value = Math.floor(Math.random() * 30) + 70
    
    console.log('✅ Performance test completed')
  } catch (error) {
    console.error('❌ Performance test failed:', error)
  } finally {
    testingPerformance.value = false
  }
}

const refreshMetrics = () => {
  // Simular actualización de métricas en tiempo real
  metrics.loadTime = Math.floor(Math.random() * 400) + 150
  metrics.memoryUsage = Math.floor(Math.random() * 20) + 8
  metrics.validationSpeed = Math.floor(Math.random() * 100) + 30
  
  console.log('🔄 Metrics refreshed')
}

const exportMetrics = () => {
  const metricsData = {
    timestamp: new Date().toISOString(),
    performance: metrics,
    score: performanceScore.value
  }
  
  const blob = new Blob([JSON.stringify(metricsData, null, 2)], { 
    type: 'application/json' 
  })
  
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `performance-metrics-${Date.now()}.json`
  a.click()
  
  URL.revokeObjectURL(url)
  console.log('📊 Metrics exported')
}

// Simular gráfico de performance
const initChart = async () => {
  await nextTick()
  
  if (!performanceChart.value) return
  
  const ctx = performanceChart.value.getContext('2d')
  if (!ctx) return
  
  // Simular datos de timeline
  const gradient = ctx.createLinearGradient(0, 0, 0, 300)
  gradient.addColorStop(0, 'rgba(102, 126, 234, 0.8)')
  gradient.addColorStop(1, 'rgba(102, 126, 234, 0.1)')
  
  ctx.fillStyle = gradient
  ctx.fillRect(0, 0, performanceChart.value.width, performanceChart.value.height)
  
  // Dibujar línea de performance simulada
  ctx.strokeStyle = '#667eea'
  ctx.lineWidth = 3
  ctx.beginPath()
  
  const points = 20
  const width = performanceChart.value.width
  const height = performanceChart.value.height
  
  for (let i = 0; i < points; i++) {
    const x = (i / (points - 1)) * width
    const y = height - (Math.sin(i * 0.5) * 50 + Math.random() * 100 + 100)
    
    if (i === 0) {
      ctx.moveTo(x, y)
    } else {
      ctx.lineTo(x, y)
    }
  }
  
  ctx.stroke()
}

onMounted(() => {
  initChart()
  
  // Simular actualizaciones periódicas
  setInterval(refreshMetrics, 5000)
})
</script>

<style scoped>
.performance-metrics-center {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.bg-gradient-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.bg-gradient {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

.chart-container {
  position: relative;
  width: 100%;
}

.chart-container canvas {
  width: 100%;
  height: 100%;
}
</style>
